// WafBlockingBanner.js — PRD-56 §5.2A
// Surfaces WAF challenge-blocking on the asset-detail page when more than
// half of probed URLs are returning challenge pages instead of real responses.
// Mirrors the FirstScanBanner structure (dismissible card, CC tokens, 4px
// left accent border) with a `wbb-` class prefix.

import React, { useEffect, useState } from 'react';
import { trackWafBannerShown, trackWafBannerCta } from '../utils/onboardingAnalytics';
import './WafBlockingBanner.css';

const VENDOR_DISPLAY = {
    vercel: 'Vercel',
    cloudflare: 'Cloudflare',
    akamai: 'Akamai',
    'aws-waf': 'AWS WAF',
    imperva: 'Imperva',
    f5: 'F5',
    fastly: 'Fastly',
    azure: 'Azure',
};

function vendorLabel(type) {
    if (!type) return 'WAF';
    return VENDOR_DISPLAY[type] || 'WAF';
}

function dismissKey(assetId) {
    return `driftalarm_wafBlockingBannerDismissed:${assetId || 'global'}`;
}

// PRD-56 stability layer: prefer Display fields (miss-counter-stable) over the
// raw last reading. Falls back to raw when Display is absent (older inventory
// rows that haven't been re-scanned since the stability layer shipped).
function pickFraction(props) {
    if (typeof props.wafBlockingDisplayFraction === 'number') return props.wafBlockingDisplayFraction;
    if (typeof props.wafBlockingFraction === 'number') return props.wafBlockingFraction;
    return 0;
}
function pickType(props) {
    return props.wafBlockingDisplayType || props.wafBlockingType || null;
}

// Don't surface a banner from a tiny sample — 40% of 5 probes is statistically
// noisy and confused operators in early validation.
const WAF_MIN_SAMPLE_FOR_BANNER = 10;

function WafBlockingBanner(props) {
    const { assetId, wafBlockingSampleSize } = props;
    const [dismissed, setDismissed] = useState(() => {
        try {
            return localStorage.getItem(dismissKey(assetId)) === 'true';
        } catch {
            return false;
        }
    });

    const fraction = pickFraction(props);
    const wafBlockingType = pickType(props);

    const sampleSize = wafBlockingSampleSize || 0;
    const visible = fraction > 0.5 && sampleSize >= WAF_MIN_SAMPLE_FOR_BANNER && !dismissed;

    const severeBand = fraction > 0.9 ? 'severe' : 'partial';
    const vendor = vendorLabel(wafBlockingType);

    useEffect(() => {
        if (visible) trackWafBannerShown(assetId, wafBlockingType || 'unknown', severeBand);
    }, [visible, assetId, wafBlockingType, severeBand]);

    if (!visible) return null;

    const pctText = `${Math.round(fraction * 100)}%`;
    const sampleText = sampleSize.toLocaleString();

    const headline = severeBand === 'severe'
        ? `${vendor} WAF challenge-blocked ${pctText} of ${sampleText} probes — coverage severely limited`
        : `${vendor} WAF blocked ${pctText} of ${sampleText} probes — partial coverage`;

    const handleDismiss = () => {
        try {
            localStorage.setItem(dismissKey(assetId), 'true');
        } catch { /* ignore quota/private-mode failures */ }
        trackWafBannerCta('dismiss', assetId);
        setDismissed(true);
        // Move focus to the page heading so keyboard users don't land in nowhere.
        const target = document.querySelector('h1, .v3-asset-title');
        if (target) {
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
        }
    };

    const mailto = 'mailto:info@driftalarm.com?subject=WAF%20Allowlist%20Request&body=Please%20share%20your%20scanner%20egress%20IPs%20so%20I%20can%20allowlist%20DriftAlarm%20in%20our%20WAF.';

    return (
        <div
            className={`wbb-banner wbb-banner--${severeBand}`}
            role="region"
            aria-labelledby="wbb-title"
        >
            <button
                type="button"
                className="wbb-dismiss"
                onClick={handleDismiss}
                aria-label="Dismiss WAF blocking notice"
                title="Dismiss"
            >
                &times;
            </button>
            <div className="wbb-icon" aria-hidden="true">⚠</div>
            <div className="wbb-content">
                <h3 id="wbb-title" className="wbb-title">{headline}</h3>
                <p className="wbb-body">
                    Your edge defenses are actively challenging our scanner. Findings reflect only
                    the WAF-bypassable portion of your attack surface — full coverage requires
                    allowlisting our scanner.
                </p>
                <div className="wbb-actions">
                    <a
                        className="wbb-cta-primary"
                        href={mailto}
                        onClick={() => trackWafBannerCta('allowlist_request', assetId)}
                    >
                        Contact us to allowlist our scanner for complete results
                    </a>
                    <a
                        className="wbb-cta-secondary"
                        href="/guides/waf-and-vulnerability-scanning"
                        onClick={() => trackWafBannerCta('learn_more', assetId)}
                    >
                        Learn more &rarr;
                    </a>
                </div>
            </div>
        </div>
    );
}

export default WafBlockingBanner;
export { vendorLabel as wafVendorLabel };
